import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, EyeOff, KeyRound, Trash2 } from 'lucide-react';
import { isPlatformHiddenSecretKey } from '@aiai/shared';
import { deletePrototypeSecret, fetchPrototypeSecrets, hidePrototypeSecret, setPrototypeSecret } from '../api';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { Callout } from './Callout';
import { SectionCard } from './SectionCard';
import { Spinner } from './Spinner';

const KEY_RE = /^[A-Z_][A-Z0-9_]{0,254}$/;

/**
 * Card to manage a prototype's encrypted environment variables for its serve
 * container. Values are stored AES-256-GCM encrypted. A secret (the default)
 * only ever shows its key name; an entry marked "Visible (not a secret)" — a
 * plain config value such as the factory setup code — shows its value inline
 * with a copy button. Changes take effect on the next deploy or relabel.
 */
export function PrototypeSecretsCard({ prototypeId }: { prototypeId: string }) {
  const queryClient = useQueryClient();
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [visible, setVisible] = useState(false);

  const secretsQuery = useQuery({
    queryKey: qk.prototypeSecrets(prototypeId),
    queryFn: () => fetchPrototypeSecrets(prototypeId),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: qk.prototypeSecrets(prototypeId) });

  const setMutation = useMutation({
    mutationFn: ({ k, v, vis }: { k: string; v: string; vis: boolean }) =>
      setPrototypeSecret(prototypeId, k, v, vis),
    onSuccess: () => {
      setKey('');
      setValue('');
      setVisible(false);
      invalidate();
    },
  });

  const hideMutation = useMutation({
    mutationFn: (k: string) => hidePrototypeSecret(prototypeId, k),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (k: string) => deletePrototypeSecret(prototypeId, k),
    onSuccess: invalidate,
  });

  const secrets = secretsQuery.data ?? [];
  const keyValid = KEY_RE.test(key);
  const platformKey = isPlatformHiddenSecretKey(key);
  const actionError = setMutation.error ?? deleteMutation.error ?? hideMutation.error;

  return (
    <SectionCard title="Environment secrets" count={secrets.length}>
      <div className="space-y-4">
        <p className="text-xs text-ink-muted">
          Encrypted env vars injected into the serve container at each deploy.
          Secret values are never returned to the client — only key names are shown; values marked visible are shown inline.
          Changes take effect on the next <span className="font-medium">Redeploy</span>.
          Full-stack prototypes get a stable <span className="font-mono">JWT_SECRET</span> automatically — overwrite it to rotate (signs everyone out).
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (keyValid && value) setMutation.mutate({ k: key, v: value, vis: visible && !platformKey });
          }}
          className="space-y-2"
        >
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              type="text"
              value={key}
              onChange={(e) => setKey(e.target.value.toUpperCase())}
              placeholder="KEY_NAME"
              aria-label="Secret key name"
              className="input flex-1 font-mono uppercase"
              spellCheck={false}
            />
            <input
              type={visible ? 'text' : 'password'}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="value"
              aria-label="Secret value"
              className="input flex-1"
            />
            <button
              type="submit"
              className="btn-secondary"
              disabled={!keyValid || !value || setMutation.isPending}
              title={
                !keyValid
                  ? 'Key must be uppercase letters, digits, and underscores (e.g. API_KEY)'
                  : undefined
              }
            >
              {setMutation.isPending ? <Spinner className="h-3.5 w-3.5" /> : 'Set'}
            </button>
          </div>
          <label className="flex items-start gap-2 text-xs text-ink-muted">
            <input
              type="checkbox"
              checked={visible && !platformKey}
              disabled={platformKey}
              onChange={(e) => setVisible(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              <span className="font-medium text-ink">Visible (not a secret)</span> — for plain config
              values you want to read back here; never use it for passwords or API keys.
            </span>
          </label>
        </form>

        {actionError && (
          <Callout tone="danger">{errorMessage(actionError, 'Secret action failed')}</Callout>
        )}

        {secretsQuery.isLoading && <p className="text-xs text-ink-faint">Loading…</p>}

        {secrets.length === 0 && !secretsQuery.isLoading ? (
          <p className="text-xs text-ink-faint">No secrets set.</p>
        ) : (
          <ul className="divide-y divide-line">
            {secrets.map((s) => (
              <li key={s.key} className="flex items-center gap-3 py-2.5">
                <KeyRound className="h-3.5 w-3.5 flex-none text-ink-faint" aria-hidden="true" />
                <span className="font-mono text-sm text-ink">{s.key}</span>
                {s.visible && s.value !== undefined ? (
                  <span className="flex min-w-0 flex-1 items-center gap-1.5">
                    <code className="truncate rounded bg-surface-2 px-1.5 py-0.5 text-xs text-ink" title={s.value}>
                      {s.value}
                    </code>
                    <button
                      type="button"
                      className="btn-ghost btn-sm"
                      onClick={() => navigator.clipboard?.writeText(s.value ?? '')}
                      title={`Copy ${s.key}`}
                    >
                      <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                      Copy
                    </button>
                    <button
                      type="button"
                      className="btn-ghost btn-sm"
                      onClick={() => hideMutation.mutate(s.key)}
                      disabled={hideMutation.isPending}
                      title={`Hide ${s.key} (treat it as a secret)`}
                    >
                      <EyeOff className="h-3.5 w-3.5" aria-hidden="true" />
                      Hide
                    </button>
                  </span>
                ) : (
                  <span className="flex-1 font-mono text-xs text-ink-faint" aria-label="hidden value">
                    ••••••••
                  </span>
                )}
                <span className="text-xs text-ink-muted">
                  {new Date(s.updatedAt).toLocaleDateString()}
                </span>
                <button
                  type="button"
                  className="btn-danger-ghost btn-sm"
                  onClick={() => deleteMutation.mutate(s.key)}
                  disabled={deleteMutation.isPending}
                  title={`Delete ${s.key}`}
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  Delete
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </SectionCard>
  );
}
